import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useEffect, useLayoutEffect, useState} from "react";
import {
    GetInvitationById, publicDeleteItemsById, publicUpdateStatusByInvitationId,
    SubmitInvitation
} from "../../../../../../reduxToolkit/Invitation/InvitationApi";
import {STEP_MODEL} from "../Model/Model";
import routes from "../../../../../../Routes/routes";
import {toast} from "react-toastify";
import {
    errorMsg, isNumericString,
    isValidDateLike,
    isValidMinMaxYear,
    isValidTimestampDateTime,
    isValidTimestampTime, MS_IN_DAY
} from "../../../../../../Apis/functions";
import FullPageLoader from "../../../../../../utils/FullPageLoader";
import SubmittingStep from "../SubComponents/SubmittingStep";
import {FIELD_TYPE} from "../../../Type/Model/Model";
import {PublicUploadImage} from "../../../../../../reduxToolkit/ShareSlice/shareApi";
import {STATUS_MODEL} from "../../../../../../enums/statusEnum";
import {PHOTO_PICKER_MODEL} from "../../../../../../components/PhotoPicker/SubComponent/Models/Models";
import {TABLES} from "../../../../../../Apis/Tables";
import {getUrlParameters, TAB_INDEX} from "../../Model/Model";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 /**
 * Custom hook to handle the documentation form flow for Invitations.
 *
 * This hook manages:
 * - Retrieval and formatting of invitation data.
 * - Dynamic field data handling and validation.
 * - Conditional rendering of form steps.
 * - Photo/document uploads.
 * - State transitions based on invitation status.
 *
 * It returns all state values and functions needed to control the documentation form.
 *
 */


export const CreateDocumentationApp= () => {
    const { id, guardFlowId } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { getInvitationById } = useSelector((state) => state.InvitationSlice);
    const [steps, setSteps] = useState(null);
    const [activeStep, setActiveStep] = useState(1);
    const [emptyFields, setEmptyFields] = useState(false);
    const [isValidData, setIsValidData] = useState({});
    const [data, setData] = useState({})
    const [photoPickerDeleted, setPhotoPickerDeleted] = useState([])
    const [photoPickerList, setPhotoPickerList] = useState([])
    const [loading, setLoading] = useState(false);
    const [btnDisabled, setBtnDisabled] = useState(false);
    const [showLoader, setShowLoader] = useState(true);
    const invitationId = getInvitationById?.invitation?.id;

    /**
     * Formats a date, time, or datetime field into a string value based on its type.
     *
     * @param {string} fieldType - The type of the field (DATE, DATE_TIME, TIME).
     * @param {Date|string} value - The value to format.
     * @returns {string|null} - The formatted string value.
     */
    const convertToMilliseconds = (fieldType, value) => {
        if (value === null || value === undefined || value === '') return null;

        const type = fieldType?.toUpperCase();
        const raw = typeof value === "string" ? value.trim() : value;
        if ((type === FIELD_TYPE.DATE.name || type === FIELD_TYPE.DATE_TIME.name)) {
            if (typeof raw === "number" && Number.isFinite(raw)) return String(raw);

            if (isNumericString(raw)) return raw;

            const date = raw instanceof Date ? raw : new Date(raw);
            if (Number.isNaN(date.getTime())) return null;
            return String(date.getTime());
        }

        if (type === FIELD_TYPE.TIME.name) {
            if (typeof raw === "number" && Number.isFinite(raw)) {
                return raw >= 0 && raw < MS_IN_DAY ? String(raw) : null;
            }

            if (isNumericString(raw)) {
                const n = Number(raw);
                return Number.isFinite(n) && n >= 0 && n < MS_IN_DAY ? String(n) : null;
            }

            const parts = String(raw).split(":");
            if (parts.length < 2 || parts.length > 3) return null;

            const hours = parseInt(parts[0], 10);
            const minutes = parseInt(parts[1], 10);
            const seconds = parts.length === 3 ? parseInt(parts[2], 10) : 0;

            if ([hours, minutes, seconds].some(Number.isNaN)) return null;
            if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) return null;

            return String(hours * 3600000 + minutes * 60000 + seconds * 1000);
        }

        return String(raw);
    };

    const isDate = (t) => [FIELD_TYPE.DATE.name, FIELD_TYPE.DATE_TIME.name].includes(t);
    const isTime = (t) => t === FIELD_TYPE.TIME.name;

    /**
     * Updates the value of a specific field in the form data.
     *
     * @param {object} field - The field object being updated.
     * @param {*} value - The new value to assign to the field.
     */
    const handleChange = (field, value) => {
        setData(prev => {
            const next = { ...prev };
            const updatedField = { ...field, value };
            const existingKey = Object.keys(next).find(
                k => next[k].fieldId === field.fieldId
            );
            if (existingKey !== undefined) {
                next[existingKey] = updatedField;
            } else {
                next[field.fieldId] = updatedField;
            }
            return next;
        });

        setIsValidData(prev => ({
            ...prev,
            [field.fieldId]: isDateTimeValueValid(value, field.fieldType),
        }));
    };

    /**
     * Validates date and time values coming from UI components (DatePicker, TimePicker, DateTimePicker).
     *
     * Validation rules:
     * - TIME fields: validated using `isValidDateLike`.
     * - DATE / DATE_TIME fields: validated using `isValidDateLike` and `isValidMinMaxYear`
     *   to ensure the year is within the allowed range.
     *
     * @param {*} value - The value to validate (Date, string, or date-like object).
     * @param {string} fieldType - The field type indicating whether the value represents
     * a DATE, TIME, or DATE_TIME.
     * @returns {boolean} Returns true if the value is valid for the given field type; otherwise false.
     */
    const isDateTimeValueValid = (value, fieldType) => {
        const isDateValue = isDate(fieldType);
        const isTimeValue = isTime(fieldType);

        if (isDateValue || isTimeValue){
            if(isTimeValue)
                return isValidDateLike(value);
            else
                return isValidDateLike(value) && isValidMinMaxYear(value);
        }
        return true;
    }

    /**
     * Validates timestamp-based values received from backend services.
     * Validation rules:
     * - TIME fields: validated using `isValidTimestampTime`.
     * - DATE / DATE_TIME fields: validated using `isValidTimestampDateTime`
     *   and `isValidMinMaxYear` to ensure the year is within the allowed range.
     *
     * @param {*} value - The timestamp value to validate (number or numeric string).
     * @param {string} fieldType - The field type indicating whether the value represents
     * a DATE, TIME, or DATE_TIME.
     * @returns {boolean} Returns true if the timestamp is valid for the given field type; otherwise false.
     */
    const isTimestampValueValid = (value, fieldType) => {
        const isDateValue = isDate(fieldType);
        const isTimeValue = isTime(fieldType);

        if (isDateValue || isTimeValue){
            if(isTimeValue)
                return isValidTimestampTime(value);
            else
                return isValidTimestampDateTime(value) && isValidMinMaxYear(value);
        }
        return true;
    }

    /**
     * Marks a selected photo field for deletion and clears its preview data.
     *
     * @param {object} selected - The photo picker field to delete.
     */
    const handleDeletePhotoPicker = (selected) => {
        setPhotoPickerList(prev =>
            prev.map(item => {
                if (item.fieldId !== selected.fieldId) return item;
                return {
                    ...item,
                    value: null
                };
            })
        );
        setPhotoPickerDeleted(prev => {
            if (prev.includes(selected.fieldSubmissionId)) {
                return prev;
            }
            return [...prev, selected.fieldSubmissionId];
        });
    };

    /**
     * retrieves data from the invitation
     */
    useEffect(() => {
        if (id) {
            if(id !== invitationId){
                dispatch(GetInvitationById(id)).then(({payload:{data:{success,data}}})=>{
                    if(success) {
                        setInvitationData(data);
                    }
                });
            }else{
                setInvitationData(getInvitationById);
            }
        }
    }, [id, invitationId, dispatch]);

    const setInvitationData = (data) => {
        const documentationStatusId = data?.invitation?.statusId;
        if(![STATUS_MODEL.PENDING_TO_UPLOAD_INFORMATION, STATUS_MODEL.CHANGES_REQUESTED].includes(documentationStatusId)){
            navigate(getUrlParameters({ url: routes.invitationDetails.registrationProcess, id: id, guardFlowId }));
            return;
        }
        if(data?.fields?.length > 0){
            if (data?.invitation?.statusId === STATUS_MODEL.CHANGES_REQUESTED){
                setData(
                    Object.fromEntries(
                        data.fields.map(f => [f.fieldId, f])
                    )
                );
            }
            data?.fields?.filter(field => field.fieldType === FIELD_TYPE.DOCUMENT.name).map(field => (
                setPhotoPickerList(prev => [...prev,{...field , value : field.value ? {...PHOTO_PICKER_MODEL,previewImage: { saved: true, name: field.value, size: field.size }} : null}])
            ))
            let aux = {};
            data?.fields?.forEach(f => {
                aux[f.fieldId] = isTimestampValueValid(f.value, f.fieldType);
            });
            setIsValidData(aux);
        }
    }

    /**
     * Control of the total steps and the number of steps to be displayed
     */
    useLayoutEffect(() => {
        setSteps({ ...STEP_MODEL.COMPLETE })
    }, [getInvitationById, activeStep]);
    /**
     * Submits the invitation form data to the API.
     * Handles different submission logic depending on whether the invitation is under review or not.
     *
     */
    const handleSubmit = async () => {
        const photoIdsWithoutFile = new Set(photoPickerList.filter(p => !p?.value?.fileSelected).map(p => p.fieldId));

        const aux =
            getInvitationById?.invitation?.statusId === STATUS_MODEL.CHANGES_REQUESTED
                ? Object.values(data)
                    .filter(field => !photoIdsWithoutFile.has(field.fieldId)).map(field => ({
                    id: field.fieldId,
                    fieldSubmissionId : field.fieldSubmissionId,
                    value: convertToMilliseconds(field.fieldType, field.value),
                    file: null
                }))
                : [
                    ...Object.values(data).map(field => ({
                        id: field.fieldId,
                        fieldSubmissionId: null,
                        value: convertToMilliseconds(field.fieldType, field.value),
                        file: null
                    })),
                    ...photoPickerList.map(field => ({
                        id: field.fieldId,
                        fieldSubmissionId: null,
                        value: field.fieldName,
                        file: null
                    }))
                ];
        dispatch(SubmitInvitation({ id, aux }))
            .unwrap()
            .then(({ data :{ data,success,code} }) => {
                if (success){
                    if(photoPickerDeleted?.length > 0)
                        dispatch(publicDeleteItemsById(photoPickerDeleted)).then(
                            ({payload: {data: { success, code }}}) => {
                                if(success) {
                                    uploadFiles(data);
                                }
                                else errorMsg(code)
                        });
                    else
                        uploadFiles(data);

                    setData({});
                    setEmptyFields(false);
                    setActiveStep(TAB_INDEX.THREE);
                } else errorMsg(code)
            });
    };
    /**
     * Handles uploading of selected photo documents to the server and updates the invitation status.
     */
    const uploadFiles = (data) => {
        if (photoPickerList?.length > 0) {
            const uploadPromises = photoPickerList
                .filter(documentField => documentField?.value?.fileSelected)
                .map(documentField => {
                    const file = documentField.value.fileSelected;
                    const documentId = data?.find(item => item.fieldId === documentField.fieldId)?.id;
                    if (!documentId) return Promise.resolve();

                    const formData = new FormData();
                    formData.append('id', documentId);
                    formData.append('option', TABLES.VISITOR_FIELD_SUBMISSIONS);
                    formData.append('file', file);

                    return dispatch(PublicUploadImage(formData));
                });

            Promise.all(uploadPromises).then(() => {
                updateInvitationStatus();
            });
        }
        else{
            updateInvitationStatus();
        }
    }

    const updateInvitationStatus = () => {
        dispatch(publicUpdateStatusByInvitationId({
            invitationId: id,
            option: STATUS_MODEL.PENDING_REVIEW,
            statusReason : 'preview_review'
        })).then(({ payload: { data: { success, code } } }) => {
            if (success) {
                dispatch(GetInvitationById(id));
                toast.success(t('status_has_been_updated'));
            } else {
                errorMsg(code);
            }
        });
    }

    /**
     * Advances to the next step in the stepper, validating current data before moving.
     */
    const handleClickNext = () => {
        const completeSteps = {
            [TAB_INDEX.ONE]: () => handleEmptyFields(),
            [TAB_INDEX.TWO]: () => handleSubmit(),
            [TAB_INDEX.THREE]: () => goCardsOptions()
        };

        const action = completeSteps[activeStep];
        if (action) action();
    };

    const goCardsOptions = () => {
        navigate(getUrlParameters({ url: routes.invitationDetails.registrationProcess, id: id, guardFlowId }));
    }

    /**
     * Validates that all required fields and photo pickers are filled before moving to the next step.
     * Shows a warning toast if any required fields are missing.
     */
    const handleEmptyFields = () => {
        const requiredFields = getInvitationById?.fields?.filter(field => field.required) || [];
        const hasInvalids = Object.values(isValidData ?? {}).some(v => v === false);//Used for date pickers, date time pickers and time pickers

        if (requiredFields.length === 0 && !hasInvalids) {
            setEmptyFields(false);
            setActiveStep(TAB_INDEX.TWO);
            return;
        }

        let aux =
            getInvitationById?.invitation?.statusId === STATUS_MODEL.CHANGES_REQUESTED
                ? Object.values(data)?.filter(field => field.fieldType !== FIELD_TYPE.DOCUMENT.name)
                : Object.values(data);

        const allEntries = [
            ...aux.filter(item => typeof item === "object" && item !== null && !Array.isArray(item) && (item.value !== null && item.value !== "")),
            ...photoPickerList
        ];

        if (allEntries.length === 0) {
            if(requiredFields.length === 0 && !hasInvalids){
                setEmptyFields(false);
                setActiveStep(TAB_INDEX.TWO);
            }else{
                if(requiredFields.length > 0){
                    setEmptyFields(true);
                    toast.warning(t('please_fill_all_fields'));
                }
                return;
            }
        }

        const requiredFieldIds = new Set(requiredFields.map(field => field.fieldId));
        const filledFieldIds = new Set(allEntries.map(entry => entry.fieldId));
        const missingFieldIds = [...requiredFieldIds].filter(id => !filledFieldIds.has(id));

        if (missingFieldIds.length > 0 || hasInvalids) {
            if(missingFieldIds.length > 0){
                setEmptyFields(true);
                toast.warning(t('please_fill_all_fields'));
            }
            return;
        }

        const missingPhotoValue = photoPickerList.filter(item => {
            if (!item.required) return false;
            if (item.value?.previewImage?.name) return false;
            const fileSelected = item?.value?.fileSelected;
            const srcSelected = item?.value?.srcSelected;

            return !fileSelected && !srcSelected;
        });

        if (missingPhotoValue.length > 0) {
            setEmptyFields(true);
            toast.warning(t('please_fill_all_fields'));
            return;
        }
        if (!missingFieldIds && !missingPhotoValue) {
            setEmptyFields(true);
            toast.warning(t('please_fill_all_fields'));
        } else {
            setEmptyFields(false);
            setActiveStep(TAB_INDEX.TWO);
        }
    };

    /**
     * Component responsible for showing a loading screen while preparing the stepper.
     *
     * @returns {JSX.Element} - Either a loading spinner or the `SubmittingStep` component.
     */
    const StepLoading = () => {
        useEffect(() => {
            const timer = setTimeout(() => {
                setShowLoader(false);
            }, 2000);

            return () => clearTimeout(timer);
        }, []);

        let title;
        let subtitle;
        if(guardFlowId){
            title = "thank_you_for_submitting_visitors_documentation";
            subtitle = "the_visitors_documentation_was_submitted_successfully";
        }else{
            title = "documentation_validation_title_submitting";
            subtitle = "documentation_validation_subtitle_submitting";
        }
        return showLoader ?<FullPageLoader className="background-linear-gradient-950-opacity025"/>
            :
            <SubmittingStep
                title={title}
                subtitle={subtitle}
                onClick={() => goCardsOptions()}
                showBtnNext={true}
            />;
    };

    return {
        t,
        guardFlowId,
        loading,
        btnDisabled,
        getInvitationById,
        steps,
        activeStep, setActiveStep,
        data,
        handleClickNext,
        handleChange,
        emptyFields,
        StepLoading,
        showLoader,
        photoPickerList,
        setPhotoPickerList,
        handleDeletePhotoPicker,
        isValidData
    }
}
